/**
 * Utility functions for handling date and time conversions.
 *
 * This module contains functions to convert Unix epoch timestamps (in seconds) to human-readable date formats.
 * It ensures consistent date formatting throughout the application. The functions rely on JavaScript's `Date`
 * object and allow for flexible, customizable date formatting.
 *
 * By using this utility, the code can easily manage dates in multiple formats without repetition.
 *
 * Functions:
 * - `convertEpochToDateTimeString`: Converts an epoch timestamp (in seconds) to a date time string in format of 'dd.MM.yyyy HH:mm:ss'.
 * - `convertEpochToDateTimeWithoutSecondsString`: Converts an epoch timestamp (in seconds) to a date time string in format of 'dd.MM.yyyy HH:mm'.
 * - `convertEpochToDateString`: Converts an epoch timestamp (in seconds) to a date string in format of 'dd.MM.yyyy'.
 * - `convertEpochToIsoDateString`: Converts an epoch timestamp (in seconds) to a date string in format of 'yyyy-MM-dd'.
 * - `convertDateToEpoch`: Converts a Date object to epoch timestamp (in seconds).
 */

export function convertEpochToDateTimeString(epochSeconds: number): string {
    // Convert epoch seconds to milliseconds for JavaScript's Date object
    const date = new Date(epochSeconds * 1000); // Convert to milliseconds

    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Add leading zero if month number is less than 10.
    const day = date.getDate().toString().padStart(2, '0'); // Add leading zero if day number is less than 10.

    const hour = date.getHours().toString().padStart(2, '0'); // Add leading zero if hour number is less than 10.
    const minute = date.getMinutes().toString().padStart(2, '0'); // Add leading zero if minutes number is less than 10.
    const seconds = date.getSeconds().toString().padStart(2, '0'); // Add leading zero if seconds number is less than 10.

    return `${day}.${month}.${year} ${hour}:${minute}:${seconds}`;
}

export function convertEpochToDateTimeWithoutSecondsString(epochSeconds: number): string {
    return convertEpochToDateTimeString(epochSeconds).replace(/(:\d{2})$/, ''); // Removes the ':seconds' part
}

export function convertEpochToDateString(epochSeconds: number): string {
    return convertEpochToDateTimeString(epochSeconds).split(' ')[0]; // Returns only the date part as 'dd.MM.yyyy'
}

export function convertEpochToIsoDateString(epochSeconds: number): string {
    const isoDateTime = new Date(epochSeconds * 1000).toISOString(); // Convert to milliseconds

    return isoDateTime.split('T')[0]; // Returns the date part of ISO string
}

export function convertDateToEpoch(date: Date) {
    return Math.floor(date.getTime() / 1000);
}
